import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Rocket, Sparkles, Handshake, MessageCircle, Briefcase, LogOut,
  Activity, Crown, ShieldCheck, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import SparkLogo from '../components/SparkLogo';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch (_) { return iso; }
};

const planLabel = (p) => {
  if (p?.is_elite) return 'Elite';
  if (p?.is_pro) return 'Pro';
  return 'Free';
};

const StatCard = ({ icon: Icon, label, value, accent = 'blue', testid }) => {
  const accentClasses = accent === 'gold'
    ? 'bg-[#ffa726]/15 text-[#ffd54f] border-[#ffa726]/25'
    : accent === 'green'
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25'
      : accent === 'pink'
        ? 'bg-pink-500/15 text-pink-300 border-pink-500/25'
        : 'bg-[#4facfe]/15 text-[#4facfe] border-[#4facfe]/25';
  return (
    <div className={`glass-card rounded-2xl p-4 sm:p-5 border ${accentClasses}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
        <Icon size={16} />
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold text-white tabular-nums" data-testid={`${testid}-value`}>
        {typeof value === 'number' ? value.toLocaleString('pt-BR') : (value ?? '—')}
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, testid }) => (
  <div className="glass-card rounded-2xl p-4 sm:p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-bold text-white">{title}</h3>
    </div>
    {children}
  </div>
);

const AdminDashboard = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total_users: 0,
    total_entrepreneurs: 0,
    total_investors: 0,
    total_businesses: 0,
    total_sparks: 0,
    total_connections: 0,
    active_users_7d: 0,
    total_messages: 0,
    total_conversations: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentBusinesses, setRecentBusinesses] = useState([]);
  const [recentSparks, setRecentSparks] = useState([]);
  const [error, setError] = useState('');

  const loadAll = async () => {
    setError('');
    try {
      // 1) Aggregate counts (in parallel)
      const [
        { count: usersCount },
        { count: entrepCount },
        { count: investorCount },
        { count: businessesCount },
        { count: sparksCount },
        { count: acceptedSparks },
        { count: messagesCount },
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'entrepreneur'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'investor'),
        supabase.from('businesses').select('*', { count: 'exact', head: true }),
        supabase.from('sparks').select('*', { count: 'exact', head: true }),
        supabase.from('sparks').select('*', { count: 'exact', head: true }).eq('status', 'accepted'),
        supabase.from('messages').select('*', { count: 'exact', head: true }).then(r => r, () => ({ count: 0 })),
      ]);

      // 7-day active users
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const { count: activeCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sevenDaysAgo);

      // distinct conversations = accepted sparks
      setStats({
        total_users: usersCount || 0,
        total_entrepreneurs: entrepCount || 0,
        total_investors: investorCount || 0,
        total_businesses: businessesCount || 0,
        total_sparks: sparksCount || 0,
        total_connections: acceptedSparks || 0,
        active_users_7d: activeCount || 0,
        total_messages: messagesCount || 0,
        total_conversations: acceptedSparks || 0,
      });

      // 2) Recent lists
      const [uRes, bRes, sRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, email, full_name, role, created_at, is_pro, is_elite, is_admin')
          .order('created_at', { ascending: false })
          .limit(15),
        supabase
          .from('businesses')
          .select('id, name, category, city, state, created_at, is_seeking, view_count, owner_id, profiles!businesses_owner_id_fkey(full_name, email)')
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('sparks')
          .select('id, status, created_at, investor_id, business_id, businesses(name), investor:profiles!sparks_investor_id_fkey(full_name, email)')
          .order('created_at', { ascending: false })
          .limit(10),
      ]);

      if (uRes.error) throw uRes.error;
      setRecentUsers(uRes.data || []);
      if (bRes.error && !bRes.error.message?.includes('relationship')) {
        // fallback plain query if the FK-join fails
        const plain = await supabase.from('businesses').select('*').order('created_at', { ascending: false }).limit(10);
        setRecentBusinesses(plain.data || []);
      } else {
        setRecentBusinesses(bRes.data || []);
      }
      if (sRes.error && !sRes.error.message?.includes('relationship')) {
        const plain = await supabase.from('sparks').select('*').order('created_at', { ascending: false }).limit(10);
        setRecentSparks(plain.data || []);
      } else {
        setRecentSparks(sRes.data || []);
      }
    } catch (e) {
      setError(e.message || 'Falha ao carregar dados administrativos.');
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      await loadAll();
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    try { await signOut(); } catch (_) {}
    // Force full teardown to avoid lingering /admin access.
    window.location.replace('/');
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050816]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <SparkLogo height={40} variant="symbol" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#ffd54f] flex items-center gap-1">
                <ShieldCheck size={11} /> GETSPARK ADMIN
              </div>
              <div className="text-sm font-bold text-white truncate">Painel administrativo</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="admin-refresh"
              className="glass-card rounded-xl px-3 py-2 text-xs font-semibold text-slate-300 hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-50"
              title="Recarregar"
            >
              <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Recarregar</span>
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-xs text-red-300 px-4 py-3" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Stat cards */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          data-testid="admin-stats"
        >
          <StatCard icon={Users} label="Total de usuários" value={loading ? '…' : stats.total_users} accent="blue" testid="stat-users" />
          <StatCard icon={Rocket} label="Empreendedores" value={loading ? '…' : stats.total_entrepreneurs} accent="gold" testid="stat-entrepreneurs" />
          <StatCard icon={Briefcase} label="Investidores" value={loading ? '…' : stats.total_investors} accent="blue" testid="stat-investors" />
          <StatCard icon={Rocket} label="Negócios publicados" value={loading ? '…' : stats.total_businesses} accent="green" testid="stat-businesses" />
          <StatCard icon={Sparkles} label="Sparks realizadas" value={loading ? '…' : stats.total_sparks} accent="pink" testid="stat-sparks" />
          <StatCard icon={Handshake} label="Conexões" value={loading ? '…' : stats.total_connections} accent="gold" testid="stat-connections" />
          <StatCard icon={MessageCircle} label="Mensagens" value={loading ? '…' : stats.total_messages} accent="blue" testid="stat-messages" />
          <StatCard icon={Activity} label="Usuários (7d)" value={loading ? '…' : stats.active_users_7d} accent="green" testid="stat-active7d" />
        </motion.section>

        {/* Recent Users */}
        <SectionCard title="Últimos registros" testid="admin-recent-users">
          {loading ? (
            <div className="text-xs text-slate-400 py-6 text-center">Carregando…</div>
          ) : recentUsers.length === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">Nenhum usuário cadastrado.</div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/5">
                    <th className="py-2 px-2">Nome</th>
                    <th className="py-2 px-2 hidden sm:table-cell">E-mail</th>
                    <th className="py-2 px-2">Tipo</th>
                    <th className="py-2 px-2">Plano</th>
                    <th className="py-2 px-2">Cadastro</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.map((u) => (
                    <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="py-2 px-2 truncate max-w-[140px] font-medium text-white">
                        {u.full_name || '—'}
                        {u.is_admin && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-[#ffa726]/15 text-[#ffd54f] border border-[#ffa726]/25 uppercase tracking-widest">
                            <ShieldCheck size={9} /> admin
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 hidden sm:table-cell truncate max-w-[220px] text-slate-300">{u.email || '—'}</td>
                      <td className="py-2 px-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                          u.role === 'investor'
                            ? 'bg-[#4facfe]/10 border-[#4facfe]/25 text-[#4facfe]'
                            : 'bg-[#ffa726]/10 border-[#ffa726]/25 text-[#ffd54f]'
                        }`}>
                          {u.role === 'investor' ? 'Investidor' : 'Empreendedor'}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                          u.is_elite
                            ? 'bg-purple-500/10 border-purple-500/25 text-purple-300'
                            : u.is_pro
                              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                              : 'bg-white/[0.03] border-white/10 text-slate-400'
                        }`}>
                          {(u.is_elite || u.is_pro) && <Crown size={9} />} {planLabel(u)}
                        </span>
                      </td>
                      <td className="py-2 px-2 whitespace-nowrap text-slate-400">{formatDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* Recent Businesses */}
        <SectionCard title="Últimos negócios" testid="admin-recent-businesses">
          {loading ? (
            <div className="text-xs text-slate-400 py-6 text-center">Carregando…</div>
          ) : recentBusinesses.length === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">Nenhum negócio publicado ainda.</div>
          ) : (
            <ul className="space-y-2">
              {recentBusinesses.map((b) => (
                <li key={b.id} className="glass-card rounded-xl px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white truncate">{b.name || 'Sem nome'}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {(b.category || '—')}{b.city ? ` • ${b.city}${b.state ? '/' + b.state : ''}` : ''}
                      {b.profiles?.full_name && <span> • por {b.profiles.full_name}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] text-slate-400 whitespace-nowrap">{formatDate(b.created_at)}</div>
                    <div className={`text-[10px] uppercase tracking-widest ${b.is_seeking === false ? 'text-slate-500' : 'text-emerald-300'}`}>
                      {b.is_seeking === false ? 'pausado' : 'ativo'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Recent Sparks */}
        <SectionCard title="Últimas Sparks" testid="admin-recent-sparks">
          {loading ? (
            <div className="text-xs text-slate-400 py-6 text-center">Carregando…</div>
          ) : recentSparks.length === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">Nenhuma Spark registrada.</div>
          ) : (
            <ul className="space-y-2">
              {recentSparks.map((s) => (
                <li key={s.id} className="glass-card rounded-xl px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">
                      <span className="font-bold">{s.investor?.full_name || s.investor?.email || 'Investidor'}</span>
                      <span className="text-slate-400"> → </span>
                      <span className="font-semibold text-[#ffd54f]">{s.businesses?.name || 'Negócio'}</span>
                    </div>
                    <div className="text-[11px] text-slate-400">status: {s.status || 'pending'}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">{formatDate(s.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <div className="text-center text-[10px] text-slate-600 pt-4 pb-8">
          Dados carregados do Supabase em tempo real • RLS aplicada.
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
